import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { CheckCircle, Star, Users, MessageSquare, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Squircle } from "@/components/ui/squircle";

interface Event {
  id: number;
  name: string;
  date: string;
  venue: string;
  type: string;
  feedbackEnabled: boolean;
  eventLogoUrl?: string | null;
  organisationName?: string;
  organisationLogoUrl?: string | null;
}

interface FeedbackData {
  name?: string;
  enjoymentRating?: number;
  wouldAttendNext?: boolean;
  feedback?: string;
}

export default function FeedbackPage() {
  const { feedbackUrl } = useParams<{ feedbackUrl: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState<FeedbackData>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Function to determine which logo to use (event -> org -> default)
  const getLogoInfo = (event: Event) => {
    // Check for event logo (not null, not undefined, not empty string)
    if (event.eventLogoUrl && event.eventLogoUrl.trim() !== '') {
      return {
        src: event.eventLogoUrl,
        alt: `${event.name} Logo`,
        isSquircle: true
      };
    }
    // Check for organization logo (not null, not undefined, not empty string)
    if (event.organisationLogoUrl && event.organisationLogoUrl.trim() !== '') {
      return {
        src: event.organisationLogoUrl,
        alt: `${event.organisationName || 'Organization'} Logo`,
        isSquircle: true
      };
    }
    // Default logo
    return {
      src: "/jam-jar.png",
      alt: "Jam Jar Logo",
      isSquircle: false
    };
  };

  // Fetch event information
  const { data: eventData, isLoading: isLoadingEvent, error: eventError } = useQuery<{ event: Event }>({
    queryKey: [`/api/feedback/event-info/${feedbackUrl}`],
    enabled: !!feedbackUrl,
    staleTime: 0
  });

  // Submit feedback mutation
  const submitFeedbackMutation = useMutation({
    mutationFn: async (data: FeedbackData & { feedbackUrl: string }) => {
      const response = await fetch('/api/feedback/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit feedback');
      }
      
      return response.json();
    },
    onSuccess: () => {
      setIsSubmitted(true);
      toast({
        title: "Feedback submitted!",
        description: "Thank you for your feedback. We appreciate your input!",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.wouldAttendNext === undefined) {
      toast({
        title: "Required field missing",
        description: "Please answer whether you would attend the next event",
        variant: "destructive",
      });
      return;
    }

    if (!feedbackUrl) return;

    await submitFeedbackMutation.mutateAsync({
      ...formData,
      feedbackUrl
    });
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'PPPP');
  };

  if (isLoadingEvent) {
    return (
      <PageBackground>
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <div className="text-lg text-slate-900 drop-shadow-sm">Loading event information...</div>
          </div>
        </div>
      </PageBackground>
    );
  }

  if (eventError || !eventData) {
    return (
      <PageBackground>
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <Card className="w-full max-w-md mx-auto bg-white/20 backdrop-blur-xl border-white/20">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <img src="/jam-jar.png" alt="Jam Jar Logo" className="h-24 w-24 drop-shadow-md"/>
                </div>
                <CardTitle className="text-slate-900">Event not found</CardTitle>
                <CardDescription className="text-slate-700">
                  This feedback link is invalid or feedback is not enabled for this event.
                </CardDescription>
              </CardHeader>

            </Card>
          </div>
        </div>
      </PageBackground>
    );
  }

  const event = eventData?.event;

  if (!event) {
    return null; // This shouldn't happen as we check for eventData above
  }

  if (isSubmitted) {
    return (
      <PageBackground>
        <div className="flex items-center justify-center relative" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="text-center relative z-10">
            <Card className="w-full max-w-md mx-auto bg-white/20 backdrop-blur-xl border-white/20">
              <CardContent className="pt-8">
                <div className="flex flex-col items-center space-y-4">
                  <div className="mb-4">
                    {(() => {
                      const logoInfo = getLogoInfo(event);
                      return logoInfo.isSquircle ? (
                        <Squircle 
                          src={logoInfo.src} 
                          alt={logoInfo.alt} 
                          size={128}
                          className="drop-shadow-md"
                        />
                      ) : (
                        <img src={logoInfo.src} alt={logoInfo.alt} className="h-32 w-32 drop-shadow-md"/>
                      );
                    })()}
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">Thank you!</h2>
                  <p className="text-slate-600 text-center">
                    Your feedback has been submitted successfully. We appreciate your input and will use it to improve future events.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative z-10">
        <div className="space-y-6">


          {/* Event Info */}
          <Card className="bg-white/20 backdrop-blur-xl border-white/20">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                {(() => {
                  const logoInfo = getLogoInfo(event);
                  return logoInfo.isSquircle ? (
                    <Squircle 
                      src={logoInfo.src} 
                      alt={logoInfo.alt} 
                      size={64}
                      className="drop-shadow-md flex-shrink-0"
                    />
                  ) : (
                    <img src={logoInfo.src} alt={logoInfo.alt} className="h-16 w-16 drop-shadow-md flex-shrink-0"/>
                  );
                })()}
                <div>
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    {event.name}
                  </CardTitle>
                  <CardDescription className="text-slate-700">
                    {event.organisationName && `Hosted by ${event.organisationName}`}
                  </CardDescription>
                </div>
              </div>
              <div className="flex flex-col gap-1 text-sm text-slate-700">
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span className="capitalize">{event.type === 'jam' ? 'Jam Night' : event.type === 'openmic' ? 'Open Mic' : event.type}</span>
                </span>
                <span>{formatDate(event.date)}</span>
                {event.venue && <span>{event.venue}</span>}
              </div>
            </CardHeader>
          </Card>

          {/* Feedback Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Your Feedback
              </CardTitle>
              <CardDescription>
                Help us improve by sharing your experience
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name (Optional) */}
                <div className="space-y-2">
                  <Label htmlFor="name">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Your Name
                      <span className="text-sm text-muted-foreground ml-1">(optional)</span>
                    </div>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name || ""}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your name (this is completely optional)"
                    className="bg-white/40 border-white/30"
                  />
                  <p className="text-xs text-muted-foreground">
                    We respect your privacy. Providing your name is entirely optional.
                  </p>
                </div>

                {/* Enjoyment Rating */}
                <div className="space-y-2">
                  <Label htmlFor="enjoyment">
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4" />
                      On a scale of 1-5, how much did you enjoy the event?
                    </div>
                  </Label>
                  <Select 
                    value={formData.enjoymentRating?.toString() || ""} 
                    onValueChange={(value) => setFormData({ ...formData, enjoymentRating: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select rating" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map(rating => (
                        <SelectItem key={rating} value={rating.toString()}>
                          {rating} {rating === 1 ? '😞' : rating === 2 ? '😕' : rating === 3 ? '😐' : rating === 4 ? '😊' : '🎉'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>



                {/* Would Attend Next */}
                <div className="space-y-2">
                  <Label>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Would you attend the next event? <span className="text-red-500">*</span>
                    </div>
                  </Label>
                  <RadioGroup
                    value={formData.wouldAttendNext?.toString() || ""}
                    onValueChange={(value) => setFormData({ ...formData, wouldAttendNext: value === "true" })}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="true" id="attend-yes" />
                      <Label htmlFor="attend-yes">Yes, I would attend</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="false" id="attend-no" />
                      <Label htmlFor="attend-no">No, I would not attend</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Free Text Feedback */}
                <div className="space-y-2">
                  <Label htmlFor="feedback">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Let us know your thoughts and suggestions!
                    </div>
                  </Label>
                  <Textarea
                    id="feedback"
                    placeholder="What did you like? What could be improved? Any suggestions for future events?"
                    value={formData.feedback || ""}
                    onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                    rows={4}
                  />
                </div>

                {/* Submit Button */}
                <div className="flex justify-center pt-4">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={submitFeedbackMutation.isPending || formData.wouldAttendNext === undefined}
                    className="min-w-32"
                  >
                    {submitFeedbackMutation.isPending ? "Submitting..." : "Submit Feedback"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </PageBackground>
  );
}